import { Dispatch } from 'redux';
import apis from '../../utils/apis';
import { showFeedbackModal } from './UserFeedbackActions';
import { RootState } from '../index';

/**
 * Deep merge preferences - merges new settings with existing ones
 * Ensures we don't overwrite existing data with empty objects
 */
const mergePreferences = (
  existing: Record<string, any>,
  updates: Record<string, any>
): Record<string, any> => {
  return {
    ...existing,
    ...updates,
  };
};

/**
 * Get complete preferences object by merging current state with updates
 * Safely handles missing or empty preference objects
 */
const getCompletePreferences = (
  currentPrefs: any,
  updates: { general_settings?: any; ai_settings?: any }
) => {
  const current = currentPrefs || {};
  const currentGeneral = current.general_settings || {};
  const currentAI = current.ai_settings || {};

  return {
    general_settings: updates.general_settings
      ? mergePreferences(currentGeneral, updates.general_settings)
      : currentGeneral,
    ai_settings: updates.ai_settings
      ? mergePreferences(currentAI, updates.ai_settings)
      : currentAI,
  };
};

/**
 * Set user preferences for both general and AI settings
 * Uses the unified /onboarding/set-preferences endpoint
 * Automatically updates Redux state with the response (preferences only)
 *
 * Important: This action only updates preferences, not other user data
 * This ensures we don't overwrite user info set by other components (like Sidebar)
 */
export const setUserPreferences = (preferences: {
  general_settings?: { language?: string; theme?: string; currency?: string };
  ai_settings?: { jurisdiction?: string; language?: string; speaker_voice?: string };
}) => async (dispatch: Dispatch<any>) => {
  try {
    const response = await apis.post('/onboarding/set-preferences', preferences);
    if (response.data.success) {
      // Update only preferences in Redux state, preserving other user data
      // This prevents clearing user info set by other components (like Sidebar)
      if (response.data.user?.preferences) {
        dispatch({
          type: 'UPDATE_USER_PREFERENCES',
          payload: {
            preferences: response.data.user.preferences
          }
        });
      }

      // dispatch(showFeedbackModal({
      //   showModal: true,
      //   message: 'Preferences updated successfully',
      //   modalType: 'success',
      //   duration: 3000
      // }));
      return response.data;
    } else {
      throw new Error(response.data.error || 'Failed to set preferences');
    }
  } catch (error: any) {
    const errorMessage = error.response?.data?.error || error.message || 'Failed to set preferences';
    dispatch(showFeedbackModal({
      showModal: true,
      message: errorMessage,
      modalType: 'error',
      duration: 5000
    }));
    throw error;
  }
};

/**
 * Fetch current user information and preferences
 * Updates Redux state with preferences only (not entire user info)
 * Uses the /onboarding/get-user-info endpoint
 * This should be called from the settings page to ensure preferences are loaded
 *
 * Important: This action only updates preferences, not other user data
 * This ensures we don't overwrite user info set by other components (like Sidebar)
 * For full user info updates during app initialization, use userInformation() from DataAction instead
 */
export const fetchUserInfoWithPreferences = () => async (dispatch: Dispatch<any>) => {
  try {
    const response = await apis.get('/onboarding/get-user-info');
    if (response.data.success) {
      // Update only preferences in Redux, preserving other user data
      // This prevents clearing user info set by other components (like Sidebar)
      if (response.data.user?.preferences) {
        dispatch({
          type: 'UPDATE_USER_PREFERENCES',
          payload: {
            preferences: response.data.user.preferences
          }
        });
      }
      return response.data;
    } else {
      throw new Error(response.data.error || 'Failed to get user info');
    }
  } catch (error: any) {
    const errorMessage = error.response?.data?.error || error.message || 'Failed to get user info';
    dispatch(showFeedbackModal({
      showModal: true,
      message: errorMessage,
      modalType: 'error',
      duration: 5000
    }));
    throw error;
  }
};

/**
 * Initialize user preferences if they are missing from the current state
 * This ensures the frontend always has the latest preferences from the backend
 */
export const initializeUserPreferencesIfNeeded = () => (dispatch: Dispatch<any>, getState: () => RootState) => {
  const state = getState();
  const preferences = state.user?.userInfo?.preferences;

  // Only fetch if preferences are missing or incomplete
  if (!preferences || (Object.keys(preferences).length === 0)) {
    return dispatch(fetchUserInfoWithPreferences() as any);
  }

  return Promise.resolve();
};

/**
 * Update general settings (language, theme, currency)
 * Intelligently merges with existing settings - never overwrites with empty objects
 * Preserves existing AI settings from state
 */
export const updateGeneralSettings = (settings: {
  language?: string;
  theme?: string;
  currency?: string;
}) => (dispatch: Dispatch<any>, getState: () => RootState) => {
  const state = getState();
  const currentPreferences = state.user?.userInfo?.preferences;

  // Get complete preferences by merging current state with updates
  const completePreferences = getCompletePreferences(currentPreferences, {
    general_settings: settings,
  });

  return dispatch(setUserPreferences(completePreferences) as any);
};

/**
 * Update AI settings (jurisdiction, language, speaker_voice)
 * Intelligently merges with existing settings - never overwrites with empty objects
 * Preserves existing general settings from state
 */
export const updateAISettings = (settings: {
  jurisdiction?: string;
  language?: string;
  speaker_voice?: string;
}) => (dispatch: Dispatch<any>, getState: () => RootState) => {
  const state = getState();
  const currentPreferences = state.user?.userInfo?.preferences;

  // Get complete preferences by merging current state with updates
  const completePreferences = getCompletePreferences(currentPreferences, {
    ai_settings: settings,
  });

  return dispatch(setUserPreferences(completePreferences) as any);
};
